/** @jsx jsx */
import { useState, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import { Input } from 'antd';
import { EditableFormTable } from '../../../_shared/editable-table';
import { ExtraEditRender, ExtraRender } from '../../../_shared/editable-table/templates';
import { TableListHeader } from '../../../_shared/table-list';
import { Button } from '../../../_shared/button';
import { size, get, orderBy} from 'lodash';
import { exportTable, guid, mergeTableEdits, searchTable, withSorters } from '../../../actions/utils';
import { getVehicles, updateVehicle, deleteVehicle } from '../../../_shared/services/vehicles.service';
import styles from './styles';

const V_EXTRAS = [
  { title: 'Cash Van', id: 'cashVan', type: 'boolean' },
  { title: 'Pickup Van', id: 'pickupVan', type: 'boolean' },
  { title: 'Maint', id: 'maint', type: 'boolean' },
  { title: 'Notes', id: 'notes', type: 'textarea' },
  { title: 'Vehicle Radius', id: 'vRadius', type: 'number' },
]

export const COLS = [
  {
    title: 'Vehicle',
    dataIndex: 'name',
    editable: true
  },
  {
    title: 'License',
    dataIndex: 'lic',
    editable: true
  },
  {
    title: 'Vin',
    dataIndex: 'vin',
    editable: true
  },
  {
    title: 'Misc',
    dataIndex: 'data',
    editable: true,
    // Card #287 — the cell stacks one line per extra behind a single dataIndex. Giving
    // the export a column per extra (off V_EXTRAS, the same list that builds the cell and
    // its editor) keeps the sheet sortable instead of one packed, unfilterable cell.
    exportColumns: V_EXTRAS.map(ve => ({
      title: ve.title,
      value: record => get(record, `data.${ve.id}`),
    })),
    customInput: (_this) => <ExtraEditRender cell={_this} extras={V_EXTRAS} />,
    render: (text, record) => <ExtraRender extras={V_EXTRAS} record={record} />
  },
]

// Card #277 round 3 — the grid's columns, each with a sorter. Misc sorts on the same text
// the search reads out of it (its five fields in the order the cell lists them). Search and
// export keep reading COLS itself.
const SORTED_COLS = withSorters(COLS);

export const Vehicles = props => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');

  const getData = async () => {
    const data = await getVehicles();
    setRows(orderBy(data, 'name'));
  }

  useEffect(() => { getData(); }, []);

  // Card #277 — the Misc cell already tells the export how to unpack itself via
  // exportColumns, so the search reads all five of its fields for free.
  const shown = searchTable(COLS, rows, search);

  const updateData = async d => {
    // EditableFormTable hands back only the rows it was given to render, which under a
    // search is a subset — so the fleet has to be stitched back together before the
    // delete diff runs, or saving one vehicle mid-search would delete every hidden one.
    const { data, removed } = mergeTableEdits(rows, shown, d);

    setRows(data);
    await Promise.all(d.map(async dd => {
      await updateVehicle(dd)
    }))

    if (!!size(removed)) {
      await Promise.all(removed.map(async dd => {
        await deleteVehicle(dd)
      }))
    }
  }

  const addRow = () => {
    // Clear the search first: the blank row has nothing to match, so it would be added
    // out of sight and the button would look broken.
    setSearch('');
    setRows([
      ...rows,
      {
        id: guid(),
        name: '',
        lic: '',
        vin: '',
        data: {}
      }
    ])
  }

  const exportData = () => {
    exportTable(COLS, rows, { sheetName: 'Vehicles' });
  }

  const rowClass = (record) => {
    if (get(record, 'data.maint') === true) {
      return 'row-warning';
    }
  }

  return (
    <div css={css(styles.table)}>
      <TableListHeader>
        <Input.Search
          placeholder="Search all columns"
          value={search}
          allowClear
          style={{ width: '40%' }}
          onChange={e => setSearch(e.target.value)}
        />
        <Button
          icon="export"
          style={{ marginLeft: 'auto' }}
          title="Export Vehicles"
          disabled={!size(rows)}
          onClick={exportData}
        />
      </TableListHeader>
      <EditableFormTable 
        data={shown}
        columns={SORTED_COLS}
        updateData={updateData}
        rowClassName={rowClass}
        addRow={addRow}
        rowKey={'id'}
        addButton={'Add Vehicle'}
      />
    </div>
  )
}